export type ViewDensity = "comfortable" | "cosy" | "dense";

export interface ViewDensityOption {
  value: ViewDensity;
  label: string;
  description: string;
}

export const DEFAULT_VIEW_DENSITY: ViewDensity = "comfortable";

export const VIEW_DENSITY_OPTIONS: ViewDensityOption[] = [
  {
    value: "comfortable",
    label: "Comfortable",
    description: "Current spacing and sizing.",
  },
  {
    value: "cosy",
    label: "Cosy",
    description: "Slightly tighter spacing without changing content.",
  },
  {
    value: "dense",
    label: "Dense",
    description: "Most compact spacing while keeping all content visible.",
  },
];

export const isViewDensity = (value: unknown): value is ViewDensity =>
  value === "comfortable" || value === "cosy" || value === "dense";

export const normalizeViewDensity = (value: unknown): ViewDensity =>
  isViewDensity(value) ? value : DEFAULT_VIEW_DENSITY;

export const getViewDensityCssVars = (density: ViewDensity): Record<string, string> => {
  switch (density) {
    case "dense":
      return {
        "--density-page-padding": "16px",
        "--density-panel-padding": "12px",
        "--density-section-gap": "10px",
        "--density-inline-gap": "6px",
        "--density-toolbar-padding-block": "6px",
        "--density-toolbar-padding-inline": "8px",
        "--density-toolbar-gap": "8px",
        "--density-toolbar-action-size": "34px",
        "--density-control-min-height": "34px",
        "--density-icon-button-size": "34px",
        "--density-button-padding-block": "3px",
        "--density-form-field-margin": "10px",
        "--density-input-padding-block": "8px",
        "--density-input-padding-inline": "10px",
        "--density-input-label-offset-x": "10px",
        "--density-input-label-offset-y": "9px",
        "--density-control-line-height": "1.25",
        "--density-phone-input-height": "36px",
        "--density-phone-input-padding-left": "48px",
        "--density-phone-flag-width": "38px",
        "--density-phone-flag-padding-left": "8px",
        "--density-phone-flag-scale": "0.86",
        "--density-choice-control-padding": "3px",
        "--density-choice-icon-size": "18px",
        "--density-choice-row-gap": "2px",
        "--density-choice-row-gap-half": "1px",
        "--density-choice-row-min-height": "28px",
        "--density-table-cell-padding-block": "5px",
        "--density-table-cell-padding-inline": "8px",
        "--density-list-item-padding-block": "5px",
        "--density-list-container-padding-block": "3px",
        "--density-list-item-min-height": "32px",
        "--density-list-item-padding-inline": "8px",
        "--density-tree-item-padding-block": "4px",
        "--density-tree-container-padding-block": "2px",
        "--density-tree-item-padding-inline": "8px",
        "--density-headline-font-size": "17px",
        "--density-body-font-size": "0.88rem",
        "--density-control-font-size": "0.88rem",
        "--density-label-font-size": "0.78rem",
        "--density-button-font-size": "0.78rem",
        "--density-table-font-size": "0.82rem",
        "--density-caption-font-size": "0.68rem",
        "--density-detail-bar-header-height": "32px",
        "--density-detail-bar-padding": "8px",
        "--density-detail-bar-gap": "6px",
        "--density-card-padding": "10px",
        "--density-overlay-body-padding": "9px",
        "--density-overlay-body-gap": "6px",
        "--density-icon-button-padding": "3px",
        "--density-form-field-margin-half": "5px",
        "--density-detail-bar-gap-half": "3px",
        "--density-result-row-padding-block": "3px",
        "--density-result-row-padding-inline": "6px",
      };
    case "cosy":
      return {
        "--density-page-padding": "20px",
        "--density-panel-padding": "14px",
        "--density-section-gap": "12px",
        "--density-inline-gap": "7px",
        "--density-toolbar-padding-block": "8px",
        "--density-toolbar-padding-inline": "10px",
        "--density-toolbar-gap": "10px",
        "--density-toolbar-action-size": "36px",
        "--density-control-min-height": "38px",
        "--density-icon-button-size": "36px",
        "--density-button-padding-block": "4px",
        "--density-form-field-margin": "12px",
        "--density-input-padding-block": "11px",
        "--density-input-padding-inline": "12px",
        "--density-input-label-offset-x": "12px",
        "--density-input-label-offset-y": "12px",
        "--density-control-line-height": "1.28",
        "--density-phone-input-height": "44px",
        "--density-phone-input-padding-left": "54px",
        "--density-phone-flag-width": "44px",
        "--density-phone-flag-padding-left": "9px",
        "--density-phone-flag-scale": "0.93",
        "--density-choice-control-padding": "5px",
        "--density-choice-icon-size": "20px",
        "--density-choice-row-gap": "4px",
        "--density-choice-row-gap-half": "2px",
        "--density-choice-row-min-height": "34px",
        "--density-table-cell-padding-block": "7px",
        "--density-table-cell-padding-inline": "10px",
        "--density-list-item-padding-block": "7px",
        "--density-list-container-padding-block": "4px",
        "--density-list-item-min-height": "38px",
        "--density-list-item-padding-inline": "10px",
        "--density-tree-item-padding-block": "6px",
        "--density-tree-container-padding-block": "3px",
        "--density-tree-item-padding-inline": "10px",
        "--density-headline-font-size": "18.5px",
        "--density-body-font-size": "0.94rem",
        "--density-control-font-size": "0.94rem",
        "--density-label-font-size": "0.82rem",
        "--density-button-font-size": "0.82rem",
        "--density-table-font-size": "0.88rem",
        "--density-caption-font-size": "0.72rem",
        "--density-detail-bar-header-height": "34px",
        "--density-detail-bar-padding": "10px",
        "--density-detail-bar-gap": "8px",
        "--density-card-padding": "12px",
        "--density-overlay-body-padding": "10px",
        "--density-overlay-body-gap": "8px",
        "--density-icon-button-padding": "4px",
        "--density-form-field-margin-half": "6px",
        "--density-detail-bar-gap-half": "4px",
        "--density-result-row-padding-block": "4px",
        "--density-result-row-padding-inline": "7px",
      };
    case "comfortable":
    default:
      return {
        "--density-page-padding": "24px",
        "--density-panel-padding": "16px",
        "--density-section-gap": "16px",
        "--density-inline-gap": "8px",
        "--density-toolbar-padding-block": "10px",
        "--density-toolbar-padding-inline": "12px",
        "--density-toolbar-gap": "14px",
        "--density-toolbar-action-size": "40px",
        "--density-control-min-height": "44px",
        "--density-icon-button-size": "44px",
        "--density-button-padding-block": "5px",
        "--density-form-field-margin": "16px",
        "--density-input-padding-block": "16.5px",
        "--density-input-padding-inline": "14px",
        "--density-input-label-offset-x": "14px",
        "--density-input-label-offset-y": "18px",
        "--density-control-line-height": "1.4375",
        "--density-phone-input-height": "56px",
        "--density-phone-input-padding-left": "58px",
        "--density-phone-flag-width": "48px",
        "--density-phone-flag-padding-left": "10px",
        "--density-phone-flag-scale": "1",
        "--density-choice-control-padding": "9px",
        "--density-choice-icon-size": "24px",
        "--density-choice-row-gap": "6px",
        "--density-choice-row-gap-half": "3px",
        "--density-choice-row-min-height": "42px",
        "--density-table-cell-padding-block": "10px",
        "--density-table-cell-padding-inline": "16px",
        "--density-list-item-padding-block": "10px",
        "--density-list-container-padding-block": "5px",
        "--density-list-item-min-height": "44px",
        "--density-list-item-padding-inline": "16px",
        "--density-tree-item-padding-block": "8px",
        "--density-tree-container-padding-block": "4px",
        "--density-tree-item-padding-inline": "12px",
        "--density-headline-font-size": "20px",
        "--density-body-font-size": "1rem",
        "--density-control-font-size": "1rem",
        "--density-label-font-size": "0.875rem",
        "--density-button-font-size": "0.875rem",
        "--density-table-font-size": "0.875rem",
        "--density-caption-font-size": "0.75rem",
        "--density-detail-bar-header-height": "36px",
        "--density-detail-bar-padding": "10px",
        "--density-detail-bar-gap": "8px",
        "--density-card-padding": "16px",
        "--density-overlay-body-padding": "12px",
        "--density-overlay-body-gap": "10px",
        "--density-icon-button-padding": "4px",
        "--density-form-field-margin-half": "8px",
        "--density-detail-bar-gap-half": "4px",
        "--density-result-row-padding-block": "4px",
        "--density-result-row-padding-inline": "8px",
      };
  }
};
